import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import LayoutContainer from 'components/layout/LayoutContainer';
import AdminHeader from 'components/header';
import { Col, Row } from 'react-bootstrap';
import Card from 'components/inputs/Card';
import * as _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import Footer from 'components/general/Footer';
import ReactPagination from 'components/general/ReactPagination';
import { GET_DEALER_ENTITLEMENT, FETCH_DEALER_CREDIT_TRANSACTIONS } from 'actions/dealerEntitlement';

const fmtDate = (d) => (d ? moment(d).format('DD/MM/YYYY') : '—');
const fmtDateTime = (d) => (d ? moment(d).format('DD/MM/YYYY HH:mm') : '—');
// Paise matter: 10% of ₹81 is ₹8.10 and must not be rounded away.
const money = (v) => (_.isNil(v) ? '—' : `₹${Number(v).toFixed(2)}`);
const value = (v) => (_.isNil(v) || v === '' ? '—' : v);

const Field = ({ label, children, md = 4 }) => (
  <Col md={md} className="mb-2">
    <div className="ec__field-label">{label}</div>
    <div className="ec__field-value">{children}</div>
  </Col>
);

Field.propTypes = {
  label: PropTypes.string,
  children: PropTypes.node,
  md: PropTypes.number,
};

const Section = ({ title, children }) => (
  <Card>
    <div className="data-report__inner">
      <div className="ec__section-title mb-2">{title}</div>
      <Row>{children}</Row>
    </div>
  </Card>
);

Section.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
};

/**
 * One sale: everything the dealer sent, the lifecycle, and the credit
 * statement for this vehicle. The movements answer "where did the money go",
 * which is the question the detail view is usually opened to settle.
 */
const EnrollmentDetail = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { dealerId, entitlementId } = useParams();

  const detail = useSelector((state) => state.dealerEntitlement.entitlementDetail);
  const transactions = useSelector((state) => state.dealerEntitlement.transactions);
  const stats = useSelector((state) => state.dealerEntitlement.transactionStats);
  const isLoading = useSelector((state) => state.dealerEntitlement.isLoading);
  const page = useSelector((state) => state.dealerEntitlement.txPage);
  const limit = useSelector((state) => state.dealerEntitlement.txLimit);
  const totalPages = useSelector((state) => state.dealerEntitlement.txTotalPages);
  const total = useSelector((state) => state.dealerEntitlement.txTotal);

  const getTransactions = useCallback(
    (data = {}) => {
      dispatch({
        type: FETCH_DEALER_CREDIT_TRANSACTIONS.REQUEST,
        payload: { ...data, entitlement: entitlementId },
      });
    },
    [entitlementId]
  );

  useEffect(() => {
    dispatch({ type: GET_DEALER_ENTITLEMENT.REQUEST, payload: entitlementId });
    getTransactions();
  }, [entitlementId]);

  const creditCards = [
    { key: 'balance', label: t('energyCreditTx.outstanding', 'Balance'), value: money(_.get(stats, 'balance.outstanding')) },
    { key: 'released', label: t('energyCreditTx.released', 'Released'), value: money(_.get(stats, 'movement.released')) },
    { key: 'used', label: t('energyCreditTx.used', 'Used'), value: money(_.get(stats, 'movement.used')) },
    { key: 'expired', label: t('energyCreditTx.expired', 'Expired'), value: money(_.get(stats, 'movement.expired')) },
    { key: 'reversed', label: t('energyCreditTx.reversed', 'Reversed'), value: money(_.get(stats, 'movement.reversed')) },
    { key: 'lifetime', label: t('energyCreditTx.lifetime', 'Lifetime'), value: money(_.get(stats, 'balance.lifetime_value')) },
  ];

  return (
    <React.Fragment>
      <LayoutContainer>
        <AdminHeader title={`${t('header.enrolment', 'Enrolment')} - ${_.get(detail, 'rto_no', '')}`} backTo={`/energyCredits/${dealerId}`} />
        <SkeletonTheme baseColor="#1b2a38" highlightColor="#092235">
          <div className="page-content-wrapper scrollable">
            <div className="data-report--main energy-credits__main">
              {isLoading && _.isEmpty(detail) ? (
                <Card>
                  <div className="p-4">
                    <Skeleton height={15} count={8} />
                  </div>
                </Card>
              ) : (
                <React.Fragment>
                  <Section title={t('enrollmentDetail.buyer', 'Buyer')}>
                    <Field label={t('enrollmentDetail.name', 'Name')}>{value(_.get(detail, 'buyer.name'))}</Field>
                    <Field label={t('enrollmentDetail.phone', 'Phone')}>{value(_.get(detail, 'buyer.phone'))}</Field>
                    <Field label={t('enrollmentDetail.email', 'Email')}>{value(_.get(detail, 'buyer.email'))}</Field>
                    <Field label={t('enrollmentDetail.dob', 'Date of Birth')}>{fmtDate(_.get(detail, 'buyer.date_of_birth'))}</Field>
                    <Field label={t('enrollmentDetail.gender', 'Gender')}>{value(_.get(detail, 'buyer.gender'))}</Field>
                    <Field label={t('enrollmentDetail.claimedBy', 'ChargeZone Account')}>{value(_.get(detail, 'user.name'))}</Field>
                    <Field label={t('enrollmentDetail.accountSource', 'Account Created By')}>
                      {_.get(detail, 'buyer_account_created_at')
                        ? `${t('enrollmentDetail.createdByDealer', 'Dealer enrolment')} - ${fmtDateTime(_.get(detail, 'buyer_account_created_at'))}`
                        : t('enrollmentDetail.existingAccount', 'Existing ChargeZone account')}
                    </Field>
                    <Field label={t('enrollmentDetail.currentAddress', 'Current Address')} md={6}>
                      {value(_.get(detail, 'buyer.current_address'))}
                    </Field>
                    <Field label={t('enrollmentDetail.permanentAddress', 'Permanent Address')} md={6}>
                      {value(_.get(detail, 'buyer.permanent_address'))}
                    </Field>
                  </Section>

                  <Section title={t('enrollmentDetail.vehicle', 'Vehicle')}>
                    <Field label={t('enrollmentDetail.make', 'Make')}>{value(_.get(detail, 'vehicle_details.make'))}</Field>
                    <Field label={t('enrollmentDetail.model', 'Model')}>{value(_.get(detail, 'vehicle_details.model'))}</Field>
                    <Field label={t('enrollmentDetail.year', 'Year')}>{value(_.get(detail, 'vehicle_details.year_of_manufacturing'))}</Field>
                    <Field label={t('enrollmentDetail.battery', 'Battery Capacity')}>
                      {value(_.get(detail, 'vehicle_details.battery_capacity'))}
                    </Field>
                    <Field label={t('enrollmentDetail.rto', 'Registration No')}>{value(_.get(detail, 'rto_no'))}</Field>
                    <Field label={t('enrollmentDetail.vin', 'VIN')}>{value(_.get(detail, 'vin_num'))}</Field>
                    <Field label={t('enrollmentDetail.registeredOn', 'Registered On')}>
                      {fmtDate(_.get(detail, 'vehicle_details.date_of_registration'))}
                    </Field>
                    {/* The dealer-supplied MAC is a hint; support must not treat it as identity. */}
                    <Field label={t('enrollmentDetail.expectedMac', 'MAC From Dealer (unverified)')}>
                      {value(_.get(detail, 'expected_evcc_id'))}
                    </Field>
                    <Field label={t('enrollmentDetail.verifiedMac', 'MAC Verified At Session')}>{value(_.get(detail, 'verified_evcc_id'))}</Field>
                  </Section>

                  <Section title={t('enrollmentDetail.saleAndLifecycle', 'Sale & Lifecycle')}>
                    <Field label={t('enrollmentDetail.branch', 'Branch')}>{value(_.get(detail, 'dealer_branch.name'))}</Field>
                    <Field label={t('enrollmentDetail.branchRaw', 'Branch As Sent')}>{value(_.get(detail, 'branch_name_raw'))}</Field>
                    <Field label={t('enrollmentDetail.city', 'City')}>
                      {value(_.get(detail, 'city.name') || _.get(detail, 'dealer_branch.city_raw'))}
                      {!_.get(detail, 'city') && ` (${t('enrollmentDetail.unmatched', 'not matched to master')})`}
                    </Field>
                    <Field label={t('enrollmentDetail.inductedAt', 'Inducted On')}>{fmtDate(_.get(detail, 'sale.inducted_at'))}</Field>
                    <Field label={t('enrollmentDetail.receivedAt', 'Received On')}>{fmtDate(_.get(detail, 'sale.received_at'))}</Field>
                    <Field label={t('enrollmentDetail.enrolled', 'Enrolled')}>{fmtDate(_.get(detail, 'enrolled_at'))}</Field>
                    <Field label={t('enrollmentDetail.activated', 'Activated')}>{fmtDate(_.get(detail, 'activated_at'))}</Field>
                    <Field label={t('enrollmentDetail.expires', 'Expires')}>{fmtDate(_.get(detail, 'expires_at'))}</Field>
                    <Field label={t('enrollmentDetail.status', 'Status')}>{_.startCase(_.get(detail, 'status', ''))}</Field>
                    <Field label={t('enrollmentDetail.reviewReason', 'Review Reason')}>{value(_.get(detail, 'review_reason'))}</Field>
                    <Field label={t('enrollmentDetail.programme', 'Programme')}>
                      {value(_.get(detail, 'dealer.name'))}
                      {_.get(detail, 'dealer.code') && ` (${_.get(detail, 'dealer.code')})`}
                    </Field>
                  </Section>

                  <Card>
                    <div className="data-report__inner">
                      <div className="ec__section-title mb-2">{t('enrollmentDetail.creditStatement', 'Credit Statement')}</div>
                      <div className="ec__stats">
                        {_.map(creditCards, (card) => (
                          <div className="ec__stat" key={card.key}>
                            <div className="ec__stat-label">{card.label}</div>
                            <div className="ec__stat-value">{isLoading ? <Skeleton height={15} width={70} /> : card.value}</div>
                          </div>
                        ))}
                      </div>
                      <div className="table-responsive mt-2">
                        <table className="record-list-table ec__statement-table">
                          <thead>
                            <tr>
                              <th>{t('energyCreditTx.at', 'Date')}</th>
                              <th>{t('energyCreditTx.type', 'Type')}</th>
                              <th>{t('energyCreditTx.amount', 'Amount')}</th>
                              <th>{t('energyCreditTx.year', 'Year')}</th>
                              <th>{t('energyCreditTx.station', 'Station')}</th>
                              <th>{t('energyCreditTx.booking', 'Booking ID')}</th>
                              <th>{t('energyCreditTx.invoice', 'Invoice No')}</th>
                              <th>{t('energyCreditTx.balanceAfter', 'Balance After')}</th>
                              <th>{t('energyCreditTx.note', 'Note')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {isLoading ? (
                              <tr>
                                <td colSpan={9} className="border-0">
                                  <Skeleton height={15} />
                                </td>
                              </tr>
                            ) : _.isEmpty(transactions) ? (
                              <tr>
                                <td colSpan={9} className="border-0">
                                  <div className="empty-data-block">{t('energyCreditTx.noTransactions', 'No credit transactions yet')}</div>
                                </td>
                              </tr>
                            ) : (
                              _.map(transactions, (item, index) => (
                                <tr key={`enrolment-tx-${index}`}>
                                  <td>{fmtDateTime(_.get(item, 'at'))}</td>
                                  <td>{_.startCase(_.get(item, 'reason', _.get(item, 'type', '')))}</td>
                                  <td>{money(_.get(item, 'amount'))}</td>
                                  <td>{_.get(item, 'year', '—')}</td>
                                  <td>{_.get(item, 'station.name', '—')}</td>
                                  <td className="ec__id">{value(_.get(item, 'booking'))}</td>
                                  <td className="ec__id">{value(_.get(item, 'invoice_no'))}</td>
                                  <td>{money(_.get(item, 'balance_after'))}</td>
                                  <td>{value(_.get(item, 'note'))}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                      {!isLoading && !_.isEmpty(transactions) && (
                        <ReactPagination
                          currentPage={page}
                          limit={limit}
                          total={total}
                          totalPages={totalPages}
                          handlePageClick={(pageVal) => getTransactions({ page: pageVal.selected + 1 })}
                          marginPagesDisplayed={1}
                          showDataCount={true}
                        />
                      )}
                    </div>
                  </Card>
                </React.Fragment>
              )}
            </div>
          </div>
        </SkeletonTheme>
        <Footer />
      </LayoutContainer>
    </React.Fragment>
  );
};

export default EnrollmentDetail;
